import { Component, Inject } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { NotificationType, PropertyList } from '@app/shared/interfaces';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { pad } from '@app/shared/utils/lib/date.util';
import { formatToTimeZone } from 'date-fns-timezone/src/formatToTimeZone';
import { format } from 'date-fns';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CodeActions, SmartProperty } from '../../data/smart-devices.interface';
import { SmartDevicesService } from '../../service/smart-devices.service';
@Component({
  standalone: false,
  selector: 'sbnb-dialog-add-manual-smart-code',
  templateUrl: './dialog-add-manual-smart-code.component.html',
  styleUrls: ['./dialog-add-manual-smart-code.component.scss'],
})
export class DialogAddManualSmartCodeComponent {
  saving = false;
  loading = false;

  currentDate = new Date().toISOString().split('T')[0];
  currentHours = new Date().getHours();

  start: { date: string; time: { hours: number; minutes: number } } = {
    date: this.currentDate,
    time: { hours: this.currentHours, minutes: 0 },
  };

  end: { date: string; time: { hours: number; minutes: number } } = {
    date: this.currentDate,
    time: { hours: this.currentHours, minutes: 0 },
  };

  alwaysOn = false;

  name = '';
  code = '';
  selectedProperties: SmartProperty[] = [];

  properties$: Observable<SmartProperty[]>;
  allowableActions: CodeActions[];

  codeLengthCanBeVariable = false;
  minCodeLength: number;
  maxCodeLength: number;

  propertySelectionConstraintCodeLength: number | null = null;
  propertySelectionConstraintTimezone: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<DialogAddManualSmartCodeComponent>,
    private smartService: SmartDevicesService,
    private toast: ToastNotificationsService,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      mode: 'create' | 'edit';
      reservationId?: string;
    }
  ) {
    this.loading = true;

    if (dialogData.mode === 'edit') {
      this.smartService.getReservationDetails(dialogData.reservationId).subscribe((codes) => {
        const res = codes[0];

        this.fetchProperties(res.properties);

        this.name = res.name;
        this.code = res.code;

        this.allowableActions = res.actions;

        this.properties$
          .pipe(
            tap((properties) => {
              this.selectedProperties = properties.filter((p) => res.properties.map((prop) => prop.id).includes(p.key));

              if (this.selectedProperties[0]) {
                this.propertySelectionConstraintCodeLength = this.selectedProperties[0].code_length;
                this.propertySelectionConstraintTimezone = this.selectedProperties[0].timezone;
              }
            })
          )
          .subscribe();

        if (!res.start && !res.end) {
          // its an always on code
          this.alwaysOn = true;
        } else {
          this.start = {
            date: format(res.start, 'YYYY-MM-DD'),
            time: {
              hours: new Date(res.start).getHours(),
              minutes: new Date(res.start).getMinutes(),
            },
          };
          this.end = {
            date: format(res.end, 'YYYY-MM-DD'),
            time: {
              hours: new Date(res.end).getHours(),
              minutes: new Date(res.end).getMinutes(),
            },
          };
        }
      });
    } else {
      this.fetchProperties();
    }
  }

  isSelected(property: SmartProperty): boolean {
    return this.selectedProperties.some((selected) => selected.key === property.key);
  }

  onPropertyScopeChange(event: MatCheckboxChange, property: SmartProperty) {
    if (event.checked) {
      this.selectedProperties = [...this.selectedProperties, property];

      this.propertySelectionConstraintCodeLength = property.code_length;
      this.propertySelectionConstraintTimezone = property.timezone;
    } else {
      this.selectedProperties = this.selectedProperties.filter((p) => p.key !== property.key);

      if (this.selectedProperties.length === 0) {
        this.propertySelectionConstraintCodeLength = null;
        this.propertySelectionConstraintTimezone = null;
      }
    }
  }

  startDateSelected($event: string) {
    this.start.date = $event;
  }

  startTimeSelected($event: { hours: number; minutes: number }) {
    this.start.time = $event;
  }

  endDateSelected($event: string) {
    this.end.date = $event;
  }

  endTimeSelected($event: { hours: number; minutes: number }) {
    this.end.time = $event;
  }

  onSaveClicked() {
    if (this.dialogData.mode === 'create') {
      this.createCode();
    } else {
      this.editCode();
    }
  }

  createCode() {
    this.saving = true;

    // Backend is a bit fussy and won't accept the 'Z' format from 8601, so we need to replace it with +00:00
    // TODO - improve
    const startDateTimeIso = this.ambiguousDateToPropertyTimezone(
      this.start.date,
      this.start.time,
      this.selectedProperties[0].timezone
    );

    const endDateTimeIso = this.ambiguousDateToPropertyTimezone(
      this.end.date,
      this.end.time,
      this.selectedProperties[0].timezone
    );

    this.smartService
      .createManualCode(
        this.name,
        this.code,
        startDateTimeIso,
        endDateTimeIso,
        this.alwaysOn,
        this.selectedProperties.map((p) => p.key)
      )
      .subscribe(
        (res) => {
          this.saving = false;
          this.smartService.refreshReservationsAndDevicesFromApi$.next(null);
          this.toast.open(`Code created`, 'Dismiss', NotificationType.Success, 2500);
          this.dialogRef.close(res);
        },
        () => {
          this.saving = false;
        }
      );
  }

  editCode() {
    this.saving = true;

    const startDateTimeIso = this.ambiguousDateToPropertyTimezone(
      this.start.date,
      this.start.time,
      this.selectedProperties[0].timezone
    );

    const endDateTimeIso = this.ambiguousDateToPropertyTimezone(
      this.end.date,
      this.end.time,
      this.selectedProperties[0].timezone
    );

    this.smartService
      .editCode(
        this.selectedProperties.map((p) => p.key),
        this.dialogData.reservationId,
        this.name,
        this.code,
        startDateTimeIso,
        endDateTimeIso,
        this.alwaysOn,
        this.allowableActions.includes('can_attach_to_multiple_properties')
      )
      .subscribe(
        (res) => {
          this.saving = false;
          this.smartService.refreshReservationsAndDevicesFromApi$.next(null);
          this.toast.open(`Code edited`, 'Dismiss', NotificationType.Success, 2500);
          this.dialogRef.close(res);
        },
        () => {
          this.saving = false;
        }
      );
  }

  fetchProperties(existingProperties?: Array<PropertyList>) {
    this.properties$ = this.smartService.getPropertiesWithDevices().pipe(
      tap((properties: SmartProperty[]) => {
        if (this.dialogData.mode !== 'create') {
          if (existingProperties) {
            const existingPropertyKeys = existingProperties.map((prop) => prop.id);
            this.selectedProperties = properties.filter((p) => existingPropertyKeys.includes(p.key));
          }
        }
        this.loading = false;
      })
    );
  }

  private ambiguousDateToPropertyTimezone(date: string, time: { hours: number; minutes: number }, timeZone: string) {
    return formatToTimeZone(`${date}T${pad(time.hours)}:${pad(time.minutes)}:00`, 'YYYY-MM-DD[T]HH:mm:ssZ', {
      timeZone,
      convertTimeZone: false,
      // as any - the types in the date-fns-library are incorrect, its missing 'convertTimeZone'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }
}
